/* Copyright (c) 2023 GRIP.S Medien GmbH & Co.KG */

import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import createMuiTheme from '@material-ui/core/styles/createMuiTheme';
import deepmerge from 'deepmerge';
import moment from 'moment';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import App from './App';
import './App.css';
import Login from './login';
import Api from './services/Api';
import defaultTheme from './theme';
import useCookie from './use-cookie';

const Container = ({ appName, customTheme, ...props }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [scopes, setScopes] = useState([]);
  const [, , clearCookie] = useCookie();

  const onLogin = useCallback(
    async (isLoggedIn, token, user) => {
      if (!isLoggedIn) {
        clearCookie();
      }
      const { open, close } = (
        await Api.apis.settings.settings_get({
          id: 'main',
        })
      ).obj;

      let isClosed = true;
      const openDate = moment(open);
      const closeDate = moment(close);
      const today = moment();
      if (today > openDate && today < closeDate) {
        isClosed = false;
      }
      Api.setClosed(isClosed);
      const { scopes } = user;
      Api.setAdmin(scopes && scopes.includes('admin'));
      setScopes(scopes);
      setIsLoggedIn(isLoggedIn);
    },
    [clearCookie],
  );

  const combinedTheme = useMemo(() => deepmerge(defaultTheme, customTheme), [
    customTheme,
  ]);
  const theme = useMemo(() => createMuiTheme(combinedTheme), [combinedTheme]);

  const content = useMemo(() => {
    let content = <Login appName={appName} onLogin={onLogin} />;

    if (isLoggedIn) {
      content = (
        <App appName={appName} scopes={scopes} onLogin={onLogin} {...props} />
      );
    }
    return content;
  }, [appName, isLoggedIn, onLogin, props, scopes]);

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const init = async () => {
      await Api.init();
      setIsReady(true);
    };
    if (!isReady) {
      init();
    }
  }, [isReady]);

  return (
    <Suspense fallback={'loading'}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {isReady && content}
      </MuiThemeProvider>
    </Suspense>
  );
};

export { Container as default };
